import React, { FC, useEffect, useState } from 'react';
import useQueryParams from "../../../../utils/hooks/useQueryParams";
import { H3, Span } from "../../../Text/Text";
import Button, { ButtonModeE } from '../../../Button/Button';
import { statusFilters } from '../../../../utils/helpers/statusFilters';
import { useLazyGetProjectsTotalCountQuery } from '../../../../api/projectsApi';
import CountUp from 'react-countup';
import { clss } from '../../../../utils/helpers/styleHelpers';
import { getLoggedUser } from '../../../../utils/helpers/tokenStorage';
import { AccessLevelE, ClientTypeE, StatusE, UserRoleE } from '../../../../models/enums';
import { useLocation } from 'react-router-dom';
import { useNonDesktopViewport } from '../../../../utils/hooks/useNonDesktopViewport';

import styles from './sidebarStatuses.module.scss';

type SidebarStatusesPropTypes = {
    setShowProfile: (showProfile: boolean) => void;
    isFooterHidden?: boolean;
    /** e.g. hide slide-in filter panel after picking a status (mobile/tablet). */
    onAfterStatusSelect?: () => void;
}

const SidebarStatuses: FC<SidebarStatusesPropTypes> = ({
    setShowProfile,
    isFooterHidden,
    onAfterStatusSelect,
}) => {
    const isNonDesktop = useNonDesktopViewport();
    const { user: { user } } = getLoggedUser();
    const { addQueryParam, searchParams } = useQueryParams();
    const queryStatus = searchParams.get('status');
    const [getProjectsTotalCount, { isFetching }] = useLazyGetProjectsTotalCountQuery();
    const [projectsTotalCount, setProjectsTotalCount] = useState(null);
    const [activeProjectsTotalCount, setActiveProjectsTotalCount] = useState(null);
    const [statusCounts, setStatusCounts] = useState<any[] | null>(null);

    //TODO: implement filtering campaigns (at first server side)
    const location = useLocation();
    // const isAppropriateForFilter = location.pathname.includes('deliveries');  // temp solution blocks filtering by status for campaign's page

    const handleStatusClick = (value: string) => {
        // if (isAppropriateForFilter) {
            addQueryParam({ status: value });
        // }
        if (isNonDesktop) {
            onAfterStatusSelect?.();
        }
    };

    const handleShowNumberOfProjects = async () => {
        const hideWhatsappButtonEvent = new CustomEvent('hideWhatsappButton', {
            detail: {
                hide: !statusCounts
            }
        });
        // remove count data if it's already shown
        if (statusCounts) {
            document.dispatchEvent(hideWhatsappButtonEvent);
            setProjectsTotalCount(null);
            setActiveProjectsTotalCount(null);
            setStatusCounts(null);
            return;
        }

        try {
            const { data } = await getProjectsTotalCount({});

            document.dispatchEvent(hideWhatsappButtonEvent);

            setProjectsTotalCount(data.TotalProjectCount);
            setActiveProjectsTotalCount(data.ActiveProjectCount);
            setStatusCounts(data.StatusCounts);

        } catch (e: any) {
            console.log(e);
        }
    };

    useEffect(() => {
        if (statusCounts) {
            setShowProfile(false)
        } else {
            setShowProfile(true)
        }
    }, [statusCounts]);

    return (
        <div
            className={styles.wrapper}
            // product tour's step id
            id='filter-by-status-wrapper'
        >
            {/* <H3>{isAppropriateForFilter ? 'Filter by status' : ''}</H3> */}
            <H3>Filter by status</H3>
            <ul className={clss([styles.statusList, statusCounts ? styles.expandedStatusList : ''])}>
                {statusFilters.filter(status => {
                    if (user.clientType === ClientTypeE['Post/sound production'] && status.value === StatusE.Creating) return false;
                    if ((
                        user.clientType === ClientTypeE['Advertising Agency'] ||
                        user.clientType === ClientTypeE['Post/sound production'] ||
                        user.clientType === ClientTypeE.Broadcaster
                    )
                        && user.accessLevel !== AccessLevelE.GlobalAdmin
                        && (status.value === StatusE.DownloadedByAllBroadcasters || status.value === StatusE.Deleted)) {
                        return false;
                    }
                    if (user.clientType === ClientTypeE.Broadcaster && status.value === StatusE.Creating) {
                        return false;
                    }
                    if (status.value === StatusE.Metadata) {
                        return false;
                    }
                    if (status.value === StatusE.InnerCampaign) {
                        return user.clientType === ClientTypeE.Broadcaster || user.accessLevel === AccessLevelE.GlobalAdmin;
                    }
                    if (status.value === StatusE.Rejected) {
                        return user.role === UserRoleE.AdvertisingAgency || UserRoleE.GlobalAdmin;
                    }

                    //* JMMEDIAT-624 show canceled status for all users
                    // if (status.value === StatusE.Cancelled) {
                    //     return user.role === UserRoleE.GlobalAdmin;
                    // }
                    return true;
                }).map((status, index) => {
                    const count = statusCounts?.find(({ Name }) => Name === status.value)?.Count;

                    return (
                        <li
                            key={index}
                            data-color={status.color}
                            onClick={() => handleStatusClick(status.value)}
                        >
                            <span
                                className={clss([statusCounts ? styles.dotWithData : '', styles.dot])}
                                style={{ backgroundColor: status.color }}
                            >
                                {!!statusCounts && (
                                    <CountUp end={count} duration={1} />
                                )}
                            </span>
                            <Span
                                // className={clss([queryStatus === status.value ? styles.selected : undefined, isAppropriateForFilter ? styles.underlineOnHover : ''])}
                                className={clss([queryStatus === status.value ? styles.selected : undefined, styles.underlineOnHover])}
                                // style={
                                //     /* temp solution blocks filtering by status for campaign's page*/
                                //     { cursor: isAppropriateForFilter ? 'pointer' : 'default' }
                                // }
                            >
                                {status.label}
                            </Span>
                        </li>
                    )
                })}
            </ul>
            {!!statusCounts && (
                <div className={styles.totalCountSection}>
                    <div className={styles.countWrapper}>
                        <CountUp end={projectsTotalCount || 0} duration={1} />
                        <Span>Total</Span>
                    </div>
                    <div className={styles.countWrapper}>
                        <CountUp end={activeProjectsTotalCount || 0} duration={1} />
                        <Span>Active</Span>
                    </div>
                </div>
            )}
            {!isFooterHidden && (
                <div className={styles.showButtonColorWrapper}>
                    <Button
                        onClick={handleShowNumberOfProjects}
                        mode={ButtonModeE.OUTLINED}
                        className={styles.showButtonColor}
                        disabled={isFetching}
                        loading={isFetching}
                    >
                        {statusCounts ? 'Hide' : 'Show'} number of projects
                    </Button>
                </div>
            )}
        </div>
    );
};

export default SidebarStatuses;